import {
  CONTRACT_TYPE_LABELS,
  YARD_EXPIRATION_MAX_YEARS_AHEAD,
  YARD_EXPIRATION_MIN_YEAR,
  YARD_STATE_LABELS,
} from "@etudescarre/constants";
import {
  Button,
  cn,
  DatePicker,
  Modal,
  Select,
  type SelectOption,
} from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  ArrowSquareOutIcon,
  GearIcon,
  PencilIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { toaster } from "@/app";
import { fetchAllCompanies } from "@/features/admin/components/fetch-all-companies";
import { fetchAllYards } from "@/features/admin/components/fetch-all-yards";
import { DeactivatedLabel } from "@/features/admin/contracts-panel";
import { apiClient } from "@/lib/api/client";
import { authClient } from "@/lib/auth-client";
import { formatDay } from "@/lib/utils/date";
import {
  getYardState,
  getYardToggleIntent,
  isYardToggleOn,
  yardStateBadgeClass as stateBadgeClass,
} from "./yard-state";

type Yard = {
  id: number;
  name: string;
  type: "lots_separes" | "entreprise_generale" | "conception_realisation";
  isLocked: boolean;
  expirationDate: string | null;
  beforeArchivingDays: number | null;
  comissioningDate: string | null;
  moaCompany: { id: number; name: string } | null;
};

const yardTypeLabels = {
  lots_separes: "Lots séparés",
  entreprise_generale: "Entreprise générale",
  conception_realisation: "Conception-réalisation",
} as const;

const MIN_EXPIRATION_DATE = `${YARD_EXPIRATION_MIN_YEAR}-01-01`;
const MAX_EXPIRATION_DATE = `${new Date().getFullYear() + YARD_EXPIRATION_MAX_YEARS_AHEAD}-12-31`;

const yardTypeOptions: SelectOption[] = [
  { label: "Lots séparés", value: "lots_separes" },
  { label: "Entreprise générale", value: "entreprise_generale" },
  { label: "Conception-réalisation", value: "conception_realisation" },
];

type CreateYardFormData = {
  name: string;
  type: "lots_separes" | "entreprise_generale" | "conception_realisation";
  moaCompanyId: number | null;
  expirationDate: string;
  beforeArchivingDays: string;
};

type UpdateDatesFormData = Pick<
  CreateYardFormData,
  "expirationDate" | "beforeArchivingDays"
>;

type ToggleAction =
  | { kind: "commission"; yard: Yard }
  | { kind: "interrupt"; yard: Yard }
  | { kind: "resume"; yard: Yard }
  | { kind: "cloture_info"; yard: Yard };

const toggleActionLabels = {
  commission: "Mettre en service",
  interrupt: "Interrompre",
  resume: "Remettre en service",
} as const;

type YardEmployee = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  isActive: boolean;
  hasYardAccess: boolean;
};

// Shown under the user before any click: what impersonating them will show.
const noYardAccessLabel =
  "N'a plus accès à ce chantier : sa sélection de chantier ne l'ouvre plus, comme pour lui.";

type ContractType =
  | "amo"
  | "ct"
  | "sps"
  | "moe"
  | "travaux_n_1"
  | "travaux_n_2";

type YardEmployeeGroup = {
  category: "donneurs_ordre" | "travaux_n_1" | "travaux_n_2";
  contracts: {
    id: number;
    type: ContractType;
    performerName: string;
    employees: YardEmployee[];
  }[];
};

const employeeCategoryLabels: Record<YardEmployeeGroup["category"], string> = {
  donneurs_ordre: "Donneurs d'ordre",
  travaux_n_1: "Travaux rang 1",
  travaux_n_2: "Travaux rang 2",
};

// Shown next to the company name in the "donneurs d'ordre" group only.
const missionLabels: Partial<Record<ContractType, string>> = {
  moe: CONTRACT_TYPE_LABELS.moe,
  amo: CONTRACT_TYPE_LABELS.amo,
  ct: CONTRACT_TYPE_LABELS.ct,
  sps: CONTRACT_TYPE_LABELS.sps,
};

export function StaffHome() {
  const navigate = useNavigate();
  const [yards, setYards] = useState<Yard[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedType, setSelectedType] = useState<
    "lots_separes" | "entreprise_generale" | "conception_realisation"
  >("lots_separes");
  const [companyOptions, setCompanyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedMoaId, setSelectedMoaId] = useState<string>("");
  const [toggleAction, setToggleAction] = useState<ToggleAction | null>(null);
  const [isToggling, setIsToggling] = useState(false);
  const [updateDatesYard, setUpdateDatesYard] = useState<Yard | null>(null);
  const [isUpdatingDates, setIsUpdatingDates] = useState(false);
  const [renameYard, setRenameYard] = useState<Yard | null>(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [impersonateYard, setImpersonateYard] = useState<Yard | null>(null);
  const [employeeGroups, setEmployeeGroups] = useState<YardEmployeeGroup[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);
  const [impersonateWrite, setImpersonateWrite] = useState(false);

  const { register, handleSubmit, reset } = useForm<CreateYardFormData>({
    defaultValues: { name: "", expirationDate: "", beforeArchivingDays: "" },
  });

  // Separate form instance from the "create yard" one above: both modals would
  // otherwise register fields under the same names ("expirationDate",
  // "beforeArchivingDays") on a single react-hook-form instance, which is
  // unsupported and led to unreliable saves.
  const {
    register: registerUpdateDates,
    handleSubmit: handleSubmitUpdateDates,
    reset: resetUpdateDates,
    watch: watchUpdateDates,
    control: controlUpdateDates,
  } = useForm<UpdateDatesFormData>({
    defaultValues: { expirationDate: "", beforeArchivingDays: "" },
  });
  const updateDatesValues = watchUpdateDates();

  const {
    register: registerRename,
    handleSubmit: handleSubmitRename,
    reset: resetRename,
  } = useForm<Pick<CreateYardFormData, "name">>({
    defaultValues: { name: "" },
  });

  const fetchYards = useCallback(async () => {
    const { yards: allYards, status } = await fetchAllYards();
    if (isNil(allYards)) {
      // A 401 means the session has ended (e.g. the user just signed out): the
      // app redirects to the login screen on its own, so showing a "can't load
      // yards" toast there would be misleading.
      if (status !== 401) {
        toaster.error({
          title: "Erreur",
          description: "Impossible de charger les chantiers",
        });
      }
    } else {
      setYards(allYards.map((y) => ({ ...y, id: Number(y.id) })));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchYards();
  }, [fetchYards]);

  useEffect(() => {
    if (!createOpen) return;
    fetchAllCompanies().then(({ companies }) => {
      if (isNotNil(companies)) {
        setCompanyOptions(
          companies.map((c) => ({
            label: c.name,
            value: String(c.id),
          })),
        );
      }
    });
  }, [createOpen]);

  const handleCreate = async (formData: CreateYardFormData) => {
    setIsCreating(true);
    const { error } = await apiClient.POST("/api/admin/yards", {
      body: {
        name: formData.name,
        type: selectedType,
        moaCompanyId: selectedMoaId ? Number(selectedMoaId) : null,
        expirationDate: formData.expirationDate || null,
        beforeArchivingDays: formData.beforeArchivingDays
          ? Number(formData.beforeArchivingDays)
          : null,
      },
    });
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de créer le chantier",
      });
    } else {
      toaster.success({ title: "Chantier créé" });
      setCreateOpen(false);
      reset();
      setSelectedType("lots_separes");
      setSelectedMoaId("");
      fetchYards();
    }
    setIsCreating(false);
  };

  const handleRename = async (formData: Pick<CreateYardFormData, "name">) => {
    if (!renameYard) return;
    setIsRenaming(true);
    try {
      const { error } = await apiClient.PATCH("/api/admin/yards/{id}", {
        params: { path: { id: renameYard.id } },
        body: { name: formData.name },
      });
      if (error) {
        toaster.error({
          title: "Erreur",
          description: "Impossible de renommer le chantier",
        });
        return;
      }
      toaster.success({ title: "Chantier renommé" });
      setRenameYard(null);
      fetchYards();
    } finally {
      setIsRenaming(false);
    }
  };

  const handleToggle = (yard: Yard) => {
    const intent = getYardToggleIntent(yard);
    if (intent.kind === "needs_dates") {
      toaster.error({
        title: "Action impossible",
        description:
          "La date d'expiration et la durée d'archivage doivent être renseignées avant la mise en service",
      });
      return;
    }
    setToggleAction({ kind: intent.kind, yard });
  };

  const confirmToggle = async () => {
    if (!toggleAction || toggleAction.kind === "cloture_info") return;
    setIsToggling(true);
    const { yard, kind } = toggleAction;

    let apiError: { error: string } | null | undefined;

    if (kind === "commission") {
      const res = await apiClient.PATCH("/api/admin/yards/{id}/commission", {
        params: { path: { id: yard.id } },
      });
      apiError = res.error;
    } else if (kind === "interrupt") {
      const res = await apiClient.PATCH("/api/admin/yards/{id}/interrupt", {
        params: { path: { id: yard.id } },
      });
      apiError = res.error;
    } else if (kind === "resume") {
      const res = await apiClient.PATCH("/api/admin/yards/{id}/resume", {
        params: { path: { id: yard.id } },
      });
      apiError = res.error;
    }

    if (apiError) {
      toaster.error({ title: "Erreur", description: "L'opération a échoué" });
    } else {
      fetchYards();
    }

    setIsToggling(false);
    setToggleAction(null);
  };

  const openImpersonateModal = async (yard: Yard) => {
    setImpersonateYard(yard);
    setImpersonateWrite(false);
    setLoadingEmployees(true);
    setEmployeeGroups([]);
    const { data, error } = await apiClient.GET(
      "/api/admin/yards/{id}/employees",
      { params: { path: { id: yard.id } } },
    );
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de charger les utilisateurs du chantier",
      });
      setImpersonateYard(null);
    } else {
      setEmployeeGroups(data.data.groups);
    }
    setLoadingEmployees(false);
  };

  const handleImpersonate = async (employee: YardEmployee) => {
    if (!impersonateYard) return;
    setImpersonatingId(employee.id);
    const { error } = await authClient.admin.impersonateUser({
      userId: employee.id,
    });
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Impossible d'incarner cet utilisateur",
      });
      setImpersonatingId(null);
      return;
    }
    if (impersonateWrite) {
      const { error: writeError } = await apiClient.POST(
        "/api/impersonation/write",
      );
      if (writeError) {
        toaster.error({
          title: "Mode écriture indisponible",
          description: "L'incarnation se poursuit en lecture seule",
        });
      }
    }
    // Whatever the yard row, the impersonated user's yard selection, as after
    // their own login.
    navigate("/organizations/select");
  };

  const handleUpdateDates = async (formData: UpdateDatesFormData) => {
    if (!updateDatesYard) return;
    setIsUpdatingDates(true);
    const { error } = await apiClient.PATCH("/api/admin/yards/{id}", {
      params: { path: { id: updateDatesYard.id } },
      body: {
        expirationDate: formData.expirationDate || null,
        beforeArchivingDays: formData.beforeArchivingDays
          ? Number(formData.beforeArchivingDays)
          : null,
      },
    });
    if (error) {
      toaster.error({
        title: "Erreur",
        description: "Impossible de modifier les dates",
      });
    } else {
      toaster.success({ title: "Dates modifiées" });
      setUpdateDatesYard(null);
      resetUpdateDates();
      fetchYards();
    }
    setIsUpdatingDates(false);
  };

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-zinc-50">
        <p className="text-zinc-400">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="bg-zinc-50">
      {/* Header — sticky at the top of the app's content scroll area, not
          height-bounded: StaffHome mounts inside Layout's own h-screen /
          overflow-y-auto content area (components/layout.tsx), so bounding
          *this* root to h-screen as well makes it exactly 100vh regardless
          of how much shorter that parent actually is (Layout's own Header
          already takes real space above it) — it overflows the parent by
          that Header's height, and Layout's content area ends up scrolling
          this header behind the app's real Header instead of leaving it
          in place. `sticky` has no such dependency: it only needs a
          scrolling ancestor, which that content area already always is. */}
      <header className="sticky top-0 z-10 border-b border-zinc-200 bg-white px-6 py-4">
        <div className="mx-auto flex max-w-7xl items-center">
          <h1 className="text-lg font-semibold text-zinc-900">
            Accueil staff ChantierCarré
          </h1>
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-zinc-900">Mes chantiers</h2>
          <Button outline onClick={() => navigate("/staff/annuaire")}>
            <ArrowSquareOutIcon data-slot="icon" className="size-4" />
            Annuaire sociétés et collaborateurs
          </Button>
        </div>

        {yards.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-zinc-300 bg-white">
            <p className="text-zinc-400">Aucun chantier enregistré</p>
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {yards.map((yard) => {
              const state = getYardState(yard);
              const toggleOn = isYardToggleOn(yard);
              return (
                <div
                  key={yard.id}
                  className="flex items-center gap-4 rounded-lg border border-zinc-200 bg-white px-5 py-4"
                >
                  {/* Paramètres du chantier */}
                  <Button
                    plain
                    onClick={() => navigate(`/admin/yards/${yard.id}`)}
                    aria-label="Paramétrage du chantier"
                    title="Paramétrage"
                  >
                    <GearIcon className="size-5" />
                  </Button>

                  {/* Nom du chantier (cliquable) + MOA */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openImpersonateModal(yard)}
                        className="cursor-pointer truncate text-left font-medium text-zinc-900 hover:underline"
                        title="Incarner un utilisateur de ce chantier"
                      >
                        {yard.name}
                      </button>
                      <Button
                        plain
                        onClick={() => {
                          resetRename({ name: yard.name });
                          setRenameYard(yard);
                        }}
                        aria-label="Renommer le chantier"
                        title="Renommer le chantier"
                      >
                        <PencilIcon className="size-4" />
                      </Button>
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                          stateBadgeClass[state],
                        )}
                      >
                        {YARD_STATE_LABELS[state]}
                      </span>
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-500">
                      {yard.moaCompany ? (
                        <span>MOA : {yard.moaCompany.name}</span>
                      ) : (
                        <span className="text-zinc-300">
                          MOA non renseignée
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Mode de dévolution des marchés de travaux */}
                  <span className="hidden w-44 shrink-0 text-sm text-zinc-600 md:block">
                    {yardTypeLabels[yard.type]}
                  </span>

                  {/* ON/OFF toggle */}
                  <button
                    type="button"
                    onClick={() => handleToggle(yard)}
                    className={cn(
                      "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-zinc-500 focus:ring-offset-2",
                      toggleOn ? "bg-green-500" : "bg-zinc-300",
                    )}
                    aria-label={
                      toggleOn
                        ? "Désactiver le chantier"
                        : "Activer le chantier"
                    }
                  >
                    <span
                      className={cn(
                        "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform",
                        toggleOn ? "translate-x-5" : "translate-x-0",
                      )}
                    />
                  </button>

                  {/* Mise en service / Expiration / Archivage (clic = modifier) */}
                  <button
                    type="button"
                    onClick={() => {
                      resetUpdateDates({
                        expirationDate: yard.expirationDate
                          ? yard.expirationDate.slice(0, 10)
                          : "",
                        beforeArchivingDays: yard.beforeArchivingDays
                          ? String(yard.beforeArchivingDays)
                          : "",
                      });
                      setUpdateDatesYard(yard);
                    }}
                    aria-label="Modifier les dates"
                    title="Modifier les dates"
                    className="hidden shrink-0 cursor-pointer flex-col items-end gap-0.5 rounded px-2 py-1 text-right text-xs text-zinc-500 hover:bg-zinc-50 sm:flex"
                  >
                    {yard.comissioningDate ? (
                      <span>
                        Mise en service :{" "}
                        {new Date(yard.comissioningDate).toLocaleDateString(
                          "fr-FR",
                        )}
                      </span>
                    ) : (
                      <span className="text-zinc-300">
                        Pas encore mis en service
                      </span>
                    )}
                    {yard.expirationDate ? (
                      <span>Expiration : {formatDay(yard.expirationDate)}</span>
                    ) : (
                      <span className="text-zinc-300">
                        Date d'expiration non renseignée
                      </span>
                    )}
                    {isNotNil(yard.beforeArchivingDays) ? (
                      <span>Archivage : {yard.beforeArchivingDays} j</span>
                    ) : (
                      <span className="text-zinc-300">
                        Durée d'archivage non renseignée
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6">
          <Button onClick={() => setCreateOpen(true)}>
            <PlusIcon data-slot="icon" className="size-4" />
            Créer un chantier
          </Button>
        </div>
      </main>

      {/* Modal: Créer un chantier */}
      <Modal.Root open={createOpen} onOpenChange={(d) => setCreateOpen(d.open)}>
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Positioner>
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Créer un chantier</Modal.Title>
              </Modal.Header>
              <form onSubmit={handleSubmit(handleCreate)}>
                <Modal.Body className="space-y-4">
                  <div>
                    <label
                      htmlFor="create-name"
                      className="mb-1 block text-sm font-medium text-zinc-900"
                    >
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="create-name"
                      {...register("name")}
                      required
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      placeholder="Nom du chantier"
                    />
                  </div>
                  <div>
                    <span className="mb-1 block text-sm font-medium text-zinc-900">
                      Type <span className="text-red-500">*</span>
                    </span>
                    <Select
                      options={yardTypeOptions}
                      value={[selectedType]}
                      onValueChange={(d) => {
                        const v = d.value[0];
                        if (v) {
                          setSelectedType(
                            v as
                              | "lots_separes"
                              | "entreprise_generale"
                              | "conception_realisation",
                          );
                        }
                      }}
                      placeholder="Sélectionner un type"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="create-moa"
                      className="mb-1 block text-sm font-medium text-zinc-900"
                    >
                      MOA
                    </label>
                    <select
                      id="create-moa"
                      value={selectedMoaId}
                      onChange={(e) => setSelectedMoaId(e.target.value)}
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    >
                      <option value="">— Aucun —</option>
                      {companyOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="create-expiration"
                      className="mb-1 block text-sm font-medium text-zinc-900"
                    >
                      Date d'expiration
                    </label>
                    <input
                      id="create-expiration"
                      type="date"
                      min={MIN_EXPIRATION_DATE}
                      max={MAX_EXPIRATION_DATE}
                      {...register("expirationDate")}
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="create-archiving"
                      className="mb-1 block text-sm font-medium text-zinc-900"
                    >
                      Durée d'archivage (jours)
                    </label>
                    <input
                      id="create-archiving"
                      type="number"
                      min="0"
                      {...register("beforeArchivingDays")}
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      placeholder="ex. 365"
                    />
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    outline
                    type="button"
                    onClick={() => setCreateOpen(false)}
                    disabled={isCreating}
                  >
                    Annuler
                  </Button>
                  <Button type="submit" disabled={isCreating}>
                    {isCreating ? "Création..." : "Créer"}
                  </Button>
                </Modal.Footer>
              </form>
            </Modal.Content>
          </Modal.Positioner>
        </Modal.Portal>
      </Modal.Root>

      {/* Modal: Renommer un chantier */}
      {renameYard && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setRenameYard(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Renommer le chantier</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleSubmitRename(handleRename)}>
                  <Modal.Body>
                    <label
                      htmlFor="rename-name"
                      className="mb-1 block text-sm font-medium text-zinc-900"
                    >
                      Nom <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="rename-name"
                      {...registerRename("name")}
                      required
                      className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      placeholder="Nom du chantier"
                    />
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      outline
                      type="button"
                      onClick={() => setRenameYard(null)}
                      disabled={isRenaming}
                    >
                      Annuler
                    </Button>
                    <Button type="submit" disabled={isRenaming}>
                      {isRenaming ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                  </Modal.Footer>
                </form>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Confirmation toggle ON/OFF */}
      {toggleAction && toggleAction.kind !== "cloture_info" && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setToggleAction(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>
                    {toggleAction.kind === "interrupt"
                      ? "Interrompre le service"
                      : toggleActionLabels[toggleAction.kind]}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Modal.Description>
                    {toggleAction.kind === "commission" &&
                      "Vous êtes sur le point de mettre en service le chantier et d'en informer les utilisateurs. Assurez-vous d'avoir bien terminé son paramétrage initial avant de valider."}
                    {toggleAction.kind === "interrupt" &&
                      "Vous êtes sur le point d'interrompre le service."}
                    {toggleAction.kind === "resume" &&
                      "Vous êtes sur le point de remettre en service le chantier."}
                  </Modal.Description>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    outline
                    onClick={() => setToggleAction(null)}
                    disabled={isToggling}
                  >
                    Annuler
                  </Button>
                  <Button onClick={confirmToggle} disabled={isToggling}>
                    {isToggling
                      ? "En cours..."
                      : toggleActionLabels[toggleAction.kind]}
                  </Button>
                </Modal.Footer>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Chantier clôturé info */}
      {toggleAction?.kind === "cloture_info" && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setToggleAction(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>Chantier clôturé</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Modal.Description>
                    Vous souhaitez passer sur ON un chantier clôturé. Pour faire
                    cette action vous devez au préalable modifier la date
                    d'expiration et/ou la durée d'archivage.
                  </Modal.Description>
                </Modal.Body>
                <Modal.Footer>
                  <Button onClick={() => setToggleAction(null)}>Fermer</Button>
                </Modal.Footer>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Modifier dates */}
      {updateDatesYard && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setUpdateDatesYard(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>
                    Modifier les dates — {updateDatesYard.name}
                  </Modal.Title>
                </Modal.Header>
                <form onSubmit={handleSubmitUpdateDates(handleUpdateDates)}>
                  <Modal.Body className="space-y-4">
                    {/* Fixed to the height of the longest variant below, so
                    the modal doesn't visibly resize as the wording switches
                    between the 1-line and 2-line messages while editing. */}
                    <Modal.Description className="min-h-10">
                      {isNil(updateDatesYard.comissioningDate)
                        ? "Le chantier est en préparation."
                        : (() => {
                            const expirationChanged =
                              updateDatesValues.expirationDate !==
                              (updateDatesYard.expirationDate
                                ? updateDatesYard.expirationDate.slice(0, 10)
                                : "");
                            const archivingChanged =
                              updateDatesValues.beforeArchivingDays !==
                              (updateDatesYard.beforeArchivingDays
                                ? String(updateDatesYard.beforeArchivingDays)
                                : "");
                            if (expirationChanged && archivingChanged) {
                              return "Vous êtes sur le point de modifier la date d'expiration et la durée d'archivage.";
                            }
                            if (expirationChanged) {
                              return "Vous êtes sur le point de modifier la date d'expiration.";
                            }
                            if (archivingChanged) {
                              return "Vous êtes sur le point de modifier la durée d'archivage.";
                            }
                            return "Vous êtes sur le point de modifier la date d'expiration et/ou la durée d'archivage.";
                          })()}
                    </Modal.Description>
                    <div>
                      {/* DatePicker (internal/ui) instead of a native
                          <input type="date">: typed entry is parsed
                          directly instead of per-segment clicking, and an
                          empty value never renders as if a date were set. */}
                      <Controller
                        control={controlUpdateDates}
                        name="expirationDate"
                        render={({ field }) => (
                          <>
                            <DatePicker
                              id="update-expiration"
                              label="Date d'expiration"
                              value={field.value}
                              onValueChange={field.onChange}
                              min={MIN_EXPIRATION_DATE}
                              max={MAX_EXPIRATION_DATE}
                            />
                            {/* The line stays reserved: the popup must not
                                resize when the date is cleared. */}
                            <p className="mt-1 h-4 text-xs text-zinc-500">
                              {!field.value &&
                                "Date d'expiration non renseignée"}
                            </p>
                          </>
                        )}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="update-archiving"
                        className="mb-1 block text-sm font-medium text-zinc-900"
                      >
                        Durée d'archivage (jours)
                      </label>
                      <input
                        id="update-archiving"
                        type="number"
                        min="0"
                        {...registerUpdateDates("beforeArchivingDays")}
                        className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      outline
                      type="button"
                      onClick={() => setUpdateDatesYard(null)}
                      disabled={isUpdatingDates}
                    >
                      Annuler
                    </Button>
                    <Button type="submit" disabled={isUpdatingDates}>
                      {isUpdatingDates ? "Enregistrement..." : "Enregistrer"}
                    </Button>
                  </Modal.Footer>
                </form>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}

      {/* Modal: Incarner un utilisateur */}
      {impersonateYard && (
        <Modal.Root
          open
          onOpenChange={(d) => {
            if (!d.open) setImpersonateYard(null);
          }}
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Content>
                <Modal.Header>
                  <Modal.Title>
                    Incarner un utilisateur — {impersonateYard.name}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {loadingEmployees ? (
                    <p className="py-4 text-center text-sm text-zinc-400">
                      Chargement...
                    </p>
                  ) : employeeGroups.length === 0 ? (
                    <p className="py-4 text-center text-sm text-zinc-400">
                      Aucun collaborateur n'intervient sur ce chantier
                    </p>
                  ) : (
                    // `isolate` keeps the z-indexed sticky titles inside the
                    // scroller's stacking context. Without it, Safari 15
                    // composites them above the scroller: they hid its
                    // scrollbar and let a pixel row of the scrolled text
                    // show above the stuck titles.
                    <div className="isolate max-h-[50vh] overflow-y-auto">
                      {employeeGroups.map((group) => (
                        <section key={group.category}>
                          {/* Fixed heights: the company name sticks right under the h-8 category title */}
                          <h3 className="sticky top-0 z-20 h-8 truncate bg-zinc-100 px-2 font-semibold text-xs/8 text-zinc-700">
                            {employeeCategoryLabels[group.category]}
                          </h3>
                          {group.contracts.map((contract) => (
                            <div key={contract.id}>
                              <h4
                                className="sticky top-8 z-10 flex h-8 items-center gap-2 border-zinc-200 border-b bg-white px-2 font-medium text-sm text-zinc-900"
                                title={contract.performerName}
                              >
                                <span className="truncate">
                                  {contract.performerName}
                                </span>
                                {isNotNil(missionLabels[contract.type]) && (
                                  <span className="shrink-0 text-zinc-500">
                                    {` ${missionLabels[contract.type]}`}
                                  </span>
                                )}
                              </h4>
                              <ul className="divide-y divide-zinc-100">
                                {contract.employees.map((employee) => {
                                  const note = employee.hasYardAccess
                                    ? null
                                    : noYardAccessLabel;
                                  const noteId = `impersonation-note-${contract.id}-${employee.id}`;
                                  return (
                                    <li
                                      key={employee.id}
                                      className="flex items-center gap-3 py-3 pl-4"
                                    >
                                      <div className="min-w-0 flex-1">
                                        <p className="flex min-w-0 items-center gap-2">
                                          <span className="truncate font-medium text-sm text-zinc-900">
                                            {[
                                              employee.firstName,
                                              employee.lastName,
                                            ]
                                              .filter(Boolean)
                                              .join(" ") ||
                                              employee.email ||
                                              "Sans nom"}
                                          </span>
                                          {!employee.isActive && (
                                            <DeactivatedLabel />
                                          )}
                                        </p>
                                        <p className="truncate text-xs text-zinc-500">
                                          {employee.email ?? "—"}
                                        </p>
                                        {isNotNil(note) && (
                                          <p
                                            id={noteId}
                                            className="text-xs text-zinc-500"
                                          >
                                            {note}
                                          </p>
                                        )}
                                      </div>
                                      <Button
                                        outline
                                        onClick={() =>
                                          handleImpersonate(employee)
                                        }
                                        disabled={isNotNil(impersonatingId)}
                                        aria-describedby={
                                          isNotNil(note) ? noteId : undefined
                                        }
                                      >
                                        {impersonatingId === employee.id
                                          ? "Connexion..."
                                          : "Incarner"}
                                      </Button>
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ))}
                        </section>
                      ))}
                    </div>
                  )}
                </Modal.Body>
                <Modal.Footer className="!justify-between">
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700">
                    <input
                      type="checkbox"
                      checked={impersonateWrite}
                      onChange={(e) => setImpersonateWrite(e.target.checked)}
                      disabled={isNotNil(impersonatingId)}
                    />
                    Incarnation avec écriture (lecture seule par défaut)
                  </label>
                  <Button
                    outline
                    onClick={() => setImpersonateYard(null)}
                    disabled={isNotNil(impersonatingId)}
                  >
                    Fermer
                  </Button>
                </Modal.Footer>
              </Modal.Content>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      )}
    </div>
  );
}
